import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import AssignDialog from "./AssignDialog";
import ReviseSubmissionModal from "./ReviseSubmissionModal";
import { formatSpecDate } from "./specSheetConfig";
import { EmptyState, LoadingState } from "../../UI/States";
import { Notice } from "../ui";
import {
  fetchSubmission,
  fetchSubmissionChat,
  sendSubmissionChat,
  updateSubmissionStatus,
  submissionPdfUrl,
  uploadSubmissionAttachments,
  uploadSalesAttachments,
  attachmentUrl,
  deleteSubmissionAttachment,
  requestSubmissionApproval,
  respondSubmissionApproval,
  saveTemplate,
} from "./api";

const STATUSES = ["New", "Assigned", "In Progress", "Complete"];

const STATUS_COLORS = {
  New: "default",
  Assigned: "warning",
  "In Progress": "info",
  Complete: "success",
};

const CHAT_POLL_MS = 10000;

const ChatPanel = ({ submissionId, channel, user, emptyHint }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const countRef = useRef(0);

  const load = () => {
    fetchSubmissionChat(submissionId, channel)
      .then((list) => {
        setMessages(list);
        if (list.length !== countRef.current) {
          countRef.current = list.length;
          bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    countRef.current = 0;
    setMessages([]);
    load();
    const timer = setInterval(load, CHAT_POLL_MS);
    return () => clearInterval(timer);
  }, [submissionId, channel]);

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      const sender = {
        id: user?.id,
        name: user?.name || user?.fullName || user?.displayName || user?.userID || "",
      };
      const sent = await sendSubmissionChat(submissionId, channel, sender, message);
      setDraft("");
      setMessages((prev) => [...prev, sent]);
      countRef.current += 1;
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
    } catch (err) {
      // keep the draft so the user can retry
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && (
          <p className="app-muted pt-8 text-center text-sm">{emptyHint}</p>
        )}
        {messages.map((m) => {
          if (m.type === "approval") {
            const label =
              m.action === "requested"
                ? "sent this for sales approval"
                : m.action === "approved"
                ? "approved the work"
                : "requested changes";
            const boxClass =
              m.action === "approved"
                ? "border-emerald-500/30 bg-emerald-500/10"
                : m.action === "changes_requested"
                ? "border-rose-500/30 bg-rose-500/10"
                : "border-sky-500/30 bg-sky-500/10";
            const textClass =
              m.action === "approved"
                ? "text-emerald-800 dark:text-emerald-200"
                : m.action === "changes_requested"
                ? "text-rose-800 dark:text-rose-200"
                : "text-sky-800 dark:text-sky-200";
            return (
              <div
                key={m.id}
                className={`rounded-xl border p-3 text-xs ${boxClass}`}
              >
                <p className={`font-semibold ${textClass}`}>
                  {m.action === "approved" ? "✓" : "◉"}{" "}
                  {m.sender?.name || "Someone"} {label}
                </p>
                {m.message && (
                  <p className={`mt-1 whitespace-pre-wrap ${textClass}`}>
                    “{m.message}”
                  </p>
                )}
                <p className="app-muted mt-1.5 text-[11px]">
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}
                </p>
              </div>
            );
          }
          if (m.type === "revision") {
            return (
              <div
                key={m.id}
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs"
              >
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  ✎ {m.sender?.name || "Someone"} revised the form
                </p>
                <ul className="mt-1.5 space-y-1 text-amber-900 dark:text-amber-100">
                  {(m.changes || []).map((c, i) => (
                    <li key={i}>
                      <span className="font-medium">{c.label}:</span>{" "}
                      <span className="line-through opacity-60">{c.from}</span>{" "}
                      → {c.to}
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-[11px] text-amber-800/70 dark:text-amber-200/70">
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}
                </p>
              </div>
            );
          }
          const mine = m.sender?.id === user?.id;
          return (
            <div
              key={m.id}
              className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                  mine
                    ? "rounded-br-md bg-sky-600 text-white shadow-sm"
                    : "app-surface rounded-bl-md border"
                }`}
              >
                {!mine && (
                  <p className="app-accent mb-0.5 text-xs font-semibold">
                    {m.sender?.name || "Unknown"}
                  </p>
                )}
                <p className="whitespace-pre-wrap break-words">{m.message}</p>
              </div>
              <p className="app-muted mt-1 text-[11px]">
                {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}
              </p>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="app-divider flex items-end gap-2 border-t p-3">
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Write a message..."
          inputProps={{ "aria-label": "Message" }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          endIcon={<SendIcon />}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

// Full workspace for one submission: PDF on the left, chat on the right.
// canAssign: assigner controls (status + assignments). showInternal: internal chat tab.
const SubmissionDashboard = ({
  submissionId,
  user,
  canAssign,
  showInternal,
  onBack,
  onChanged,
}) => {
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState(null);
  const [activeChat, setActiveChat] = useState(showInternal ? "internal" : "external");
  const [assignOpen, setAssignOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [pdfVersion, setPdfVersion] = useState(0);
  const [chatRefresh, setChatRefresh] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState(null);
  // null | "request" (SBCF asks for approval) | "changes" (sales requests changes)
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [approvalNote, setApprovalNote] = useState("");
  const [approvalBusy, setApprovalBusy] = useState(false);
  const fileInputRef = useRef(null);
  const salesFileInputRef = useRef(null);
  const [salesUploading, setSalesUploading] = useState(false);
  const [salesFileError, setSalesFileError] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateSaved, setTemplateSaved] = useState(false);

  const identity = () => ({
    id: user?.id,
    name:
      user?.name || user?.fullName || user?.displayName || user?.userID || "",
  });

  useEffect(() => {
    fetchSubmission(submissionId)
      .then(setSubmission)
      .catch((err) => setError(err.message));
  }, [submissionId]);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    setFileError(null);
    try {
      const added = await uploadSubmissionAttachments(
        submissionId,
        files,
        identity()
      );
      setSubmission((s) => ({
        ...s,
        attachments: [...(s.attachments || []), ...added],
      }));
    } catch (err) {
      setFileError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSalesFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setSalesUploading(true);
    setSalesFileError(null);
    try {
      const added = await uploadSalesAttachments(
        submissionId,
        files,
        identity()
      );
      setSubmission((s) => ({
        ...s,
        sales_attachments: [...(s.sales_attachments || []), ...added],
      }));
    } catch (err) {
      setSalesFileError(err.message);
    } finally {
      setSalesUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    try {
      await deleteSubmissionAttachment(submissionId, attachment.id);
      setSubmission((s) => ({
        ...s,
        attachments: (s.attachments || []).filter((a) => a.id !== attachment.id),
      }));
    } catch (err) {
      setFileError(err.message);
    }
  };

  const handleSaveTemplate = async () => {
    setTemplateSaving(true);
    setTemplateError(null);
    try {
      await saveTemplate({
        name: templateName,
        owner: identity(),
        values: submission.values,
        formData: submission.form_data,
        products: submission.products,
      });
      setTemplateDialogOpen(false);
      setTemplateName("");
      setTemplateSaved(true);
    } catch (err) {
      setTemplateError(err.message);
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleApprovalAction = async (action) => {
    setApprovalBusy(true);
    try {
      let result;
      if (action === "request") {
        result = await requestSubmissionApproval(
          submissionId,
          identity(),
          approvalNote
        );
        setSubmission((s) => ({ ...s, approval: result.approval }));
      } else {
        const approved = action === "approve";
        result = await respondSubmissionApproval(
          submissionId,
          identity(),
          approved,
          approvalNote
        );
        setSubmission((s) => ({
          ...s,
          approval: result.approval,
          status: result.status,
        }));
      }
      setApprovalDialog(null);
      setApprovalNote("");
      setActiveChat("external");
      setChatRefresh((v) => v + 1);
      onChanged?.();
    } catch (err) {
      setFileError(err.message);
      setApprovalDialog(null);
    } finally {
      setApprovalBusy(false);
    }
  };

  const handleStatusChange = async (status) => {
    const prev = submission.status;
    setSubmission((s) => ({ ...s, status }));
    try {
      await updateSubmissionStatus(submissionId, status, identity());
      onChanged?.();
    } catch (err) {
      setSubmission((s) => ({ ...s, status: prev }));
    }
  };

  if (error) {
    return (
      <div className="app-card rounded-2xl border">
        <EmptyState
          icon={ErrorOutlineIcon}
          title="Couldn't open this spec sheet"
          description={error}
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="app-card rounded-2xl border">
        <LoadingState label="Loading submission..." />
      </div>
    );
  }

  const values = submission.values || {};
  const chatTabs = showInternal ? ["internal", "external"] : ["external"];

  // Assigners/admins plus anyone assigned to this form can revise it
  const canRevise =
    showInternal &&
    (canAssign ||
      (submission.assigned_to || []).some((a) => a.id === user?.id));

  const approval = submission.approval || { state: "none" };
  const isSubmitter = user?.id === submission.submitted_by?.id;
  const attachments = submission.attachments || [];

  const approvalChip =
    approval.state === "pending" ? (
      <Chip label="Awaiting Sales Approval" color="warning" size="small" />
    ) : approval.state === "approved" ? (
      <Chip label="Approved by Sales" color="success" size="small" />
    ) : approval.state === "changes_requested" ? (
      <Chip label="Changes Requested" color="error" size="small" />
    ) : null;

  const pdfSrc = `${submissionPdfUrl(submissionId)}?v=${pdfVersion}#toolbar=0&navpanes=0&view=FitH`;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="app-card rounded-2xl border p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:gap-3">
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} className="shrink-0">
              Back
            </Button>
            <div className="min-w-0">
              <h2 className="text-xl font-bold tracking-tight">
                {values.contractorName || "Unnamed Contractor"}
              </h2>
              <p className="app-muted text-sm">
                {values.address || "No address (pinned location)"}
                {values.state ? ` — ${values.state}` : ""}
              </p>
              <p className="app-muted mt-1 text-xs tabular-nums">
                Submitted by {submission.submitted_by?.name || "Unknown"} on{" "}
                {new Date(submission.submitted_at).toLocaleString()}
                {values.requiredDate
                  ? ` • Needed: ${formatSpecDate(values.requiredDate)}`
                  : ""}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {(submission.products || []).map((p) => (
              <Chip key={p} label={p} size="small" variant="outlined" />
            ))}
            {approvalChip}
            {isSubmitter &&
              (templateSaved ? (
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  ✓ Saved as template
                </span>
              ) : (
                <Button
                  size="small"
                  startIcon={<BookmarkAddIcon />}
                  onClick={() => setTemplateDialogOpen(true)}
                >
                  Save as Template
                </Button>
              ))}
            {canAssign ? (
              <FormControl size="small">
                <Select
                  value={submission.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  inputProps={{ "aria-label": "Status" }}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Chip
                label={submission.status}
                color={STATUS_COLORS[submission.status] || "default"}
                size="small"
              />
            )}
          </div>
        </div>

        {/* Assignments */}
        <div className="app-divider mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
          <span className="app-muted text-sm font-medium">Assigned to:</span>
          {(submission.assigned_to || []).length === 0 && (
            <span className="app-muted text-sm">Nobody yet</span>
          )}
          {(submission.assigned_to || []).map((a) => (
            <Chip key={a.id} label={a.name} size="small" />
          ))}
          {canAssign && (
            <Button
              size="small"
              startIcon={<GroupAddIcon />}
              onClick={() => setAssignOpen(true)}
            >
              Manage
            </Button>
          )}
        </div>
      </div>

      {/* Sales approval banner */}
      {isSubmitter && approval.state === "pending" && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-5 sm:flex-row sm:items-center">
          <div>
            <p className="font-semibold text-sky-800 dark:text-sky-200">
              The SBCF team submitted this for your approval
              {approval.requested_by?.name
                ? ` (${approval.requested_by.name})`
                : ""}
              .
            </p>
            <p className="text-sm text-sky-800/80 dark:text-sky-200/80">
              Review the form and attached files, then approve or request
              changes.
              {approval.note ? ` Note: “${approval.note}”` : ""}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              color="error"
              variant="outlined"
              disabled={approvalBusy}
              onClick={() => {
                setApprovalNote("");
                setApprovalDialog("changes");
              }}
            >
              Request Changes
            </Button>
            <Button
              color="success"
              variant="contained"
              startIcon={<TaskAltIcon />}
              disabled={approvalBusy}
              onClick={() => handleApprovalAction("approve")}
            >
              Approve
            </Button>
          </div>
        </div>
      )}

      {/* Files */}
      <div className="app-card rounded-2xl border p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold tabular-nums">
            Files{attachments.length > 0 ? ` (${attachments.length})` : ""}
          </h3>
          {canRevise && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.docx,.xlsx,.xls,.csv"
                onChange={handleFilesSelected}
              />
              <Button
                size="small"
                startIcon={<AttachFileIcon />}
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? "Uploading..." : "Attach Files"}
              </Button>
            </>
          )}
        </div>
        {fileError && (
          <Notice tone="rose" className="mt-3 font-medium">
            {fileError}
          </Notice>
        )}
        {attachments.length === 0 ? (
          <p className="app-muted mt-2 text-sm">
            No files attached yet.
            {canRevise
              ? " Attach truss designs, quotes, or drawings here (up to 50MB per file)."
              : ""}
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="app-surface flex items-center justify-between gap-2 rounded-xl border px-3 py-2"
              >
                <a
                  href={attachmentUrl(submissionId, a.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex min-w-0 flex-1 items-center gap-2.5"
                >
                  <InsertDriveFileOutlinedIcon
                    fontSize="small"
                    className="app-muted shrink-0"
                  />
                  <div className="min-w-0">
                  <p className="app-accent truncate text-sm font-medium group-hover:underline">
                    {a.name}
                  </p>
                  <p className="app-muted text-xs tabular-nums">
                    {(a.size / 1024).toFixed(0)} KB • {a.uploaded_by?.name} •{" "}
                    {a.uploaded_at
                      ? new Date(a.uploaded_at).toLocaleDateString()
                      : ""}
                  </p>
                  </div>
                </a>
                {canRevise && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAttachment(a)}
                    aria-label={`Delete ${a.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Body: PDF + chat, equal heights */}
      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-5">
        <div className="app-card flex h-[72vh] flex-col overflow-hidden rounded-2xl border lg:col-span-3">
          <div className="app-divider flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
            <h3 className="font-semibold">Submitted Form</h3>
            <div className="flex flex-wrap items-center gap-1">
              {canRevise &&
                approval.state !== "pending" &&
                approval.state !== "approved" && (
                  <Button
                    size="small"
                    color="success"
                    startIcon={<TaskAltIcon />}
                    onClick={() => {
                      setApprovalNote("");
                      setApprovalDialog("request");
                    }}
                  >
                    Submit for Approval
                  </Button>
                )}
              {canRevise && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setReviseOpen(true)}
                >
                  Revise
                </Button>
              )}
              <Button
                size="small"
                endIcon={<OpenInNewIcon />}
                href={submissionPdfUrl(submissionId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open PDF
              </Button>
            </div>
          </div>
          <iframe
            key={pdfVersion}
            title="Spec Sheet PDF"
            src={pdfSrc}
            className="w-full flex-1 bg-white"
          />

          {/* Attachments the salesperson uploaded (with the form or after) */}
          {((submission.sales_attachments || []).length > 0 || isSubmitter) && (
            <div className="app-divider max-h-36 overflow-y-auto border-t px-4 py-2.5">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <p className="app-eyebrow">Attachments from Sales</p>
                {isSubmitter && (
                  <>
                    <input
                      ref={salesFileInputRef}
                      type="file"
                      multiple
                      hidden
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.docx,.xlsx,.xls,.csv"
                      onChange={handleSalesFilesSelected}
                    />
                    <Button
                      size="small"
                      startIcon={<AttachFileIcon />}
                      disabled={salesUploading}
                      onClick={() => salesFileInputRef.current?.click()}
                    >
                      {salesUploading ? "Uploading..." : "Add Files"}
                    </Button>
                  </>
                )}
              </div>
              {salesFileError && (
                <p className="mb-1.5 text-xs font-medium text-rose-700 dark:text-rose-300">
                  {salesFileError}
                </p>
              )}
              {(submission.sales_attachments || []).length === 0 ? (
                <p className="app-muted text-xs">
                  No attachments yet — add blueprints, plans, or photos (up to
                  50MB per file).
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(submission.sales_attachments || []).map((a) => (
                    <a
                      key={a.id}
                      href={attachmentUrl(submissionId, a.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-chip app-accent transition-colors hover:border-sky-400"
                    >
                      {a.name}
                      <span className="app-muted tabular-nums">
                        {(a.size / 1024).toFixed(0)} KB
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="app-card flex h-[72vh] flex-col overflow-hidden rounded-2xl border lg:col-span-2">
          <div className="app-divider flex border-b">
            {chatTabs.map((channel) => (
              <button
                key={channel}
                type="button"
                onClick={() => setActiveChat(channel)}
                aria-pressed={activeChat === channel}
                className={`-mb-px flex-1 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  activeChat === channel
                    ? "app-accent border-sky-500"
                    : "app-muted border-transparent hover:text-[color:var(--app-text)]"
                }`}
              >
                {channel === "internal" ? "Internal (SBCF)" : "External (Sales)"}
              </button>
            ))}
          </div>
          <ChatPanel
            key={`${activeChat}-${pdfVersion}-${chatRefresh}`}
            submissionId={submissionId}
            channel={activeChat}
            user={user}
            emptyHint={
              activeChat === "internal"
                ? "No internal notes yet — collaborate with the SBCF team here."
                : "No messages yet — use this to talk with the salesperson who submitted the form."
            }
          />
        </div>
      </div>

      <AssignDialog
        open={assignOpen}
        submission={submission}
        onClose={() => setAssignOpen(false)}
        onSaved={(assignedTo, status) => {
          setSubmission((s) => ({
            ...s,
            assigned_to: assignedTo,
            status: status || s.status,
          }));
          onChanged?.();
        }}
      />

      {/* Save-as-template name dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Save as Template</DialogTitle>
        <DialogContent>
          <p className="app-muted mb-3 text-sm">
            Saves this form's fields and product selections so you can start
            your next spec sheet from it. Dates and attachments aren't carried
            over.
          </p>
          {templateError && (
            <Notice tone="rose" className="mb-3 font-medium">
              {templateError}
            </Notice>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Template name"
            placeholder='e.g. "Standard ranch — roof & floors"'
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={templateSaving || !templateName.trim()}
            onClick={handleSaveTemplate}
          >
            {templateSaving ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval note dialog (request approval / request changes) */}
      <Dialog
        open={!!approvalDialog}
        onClose={() => setApprovalDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {approvalDialog === "request"
            ? "Submit for Sales Approval"
            : "Request Changes"}
        </DialogTitle>
        <DialogContent>
          <p className="app-muted mb-3 text-sm">
            {approvalDialog === "request"
              ? "The salesperson will be asked to review and approve this form and its files."
              : "Tell the SBCF team what needs to change."}
          </p>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={approvalDialog === "request" ? "Note (optional)" : "What needs to change?"}
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={approvalDialog === "request" ? "success" : "error"}
            disabled={
              approvalBusy ||
              (approvalDialog === "changes" && !approvalNote.trim())
            }
            onClick={() =>
              handleApprovalAction(
                approvalDialog === "request" ? "request" : "reject"
              )
            }
          >
            {approvalBusy
              ? "Sending..."
              : approvalDialog === "request"
              ? "Submit for Approval"
              : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <ReviseSubmissionModal
        open={reviseOpen}
        submission={submission}
        user={user}
        onClose={() => setReviseOpen(false)}
        onSaved={(newValues, newFormData) => {
          setSubmission((s) => ({
            ...s,
            values: newValues,
            form_data: newFormData,
          }));
          // Re-render the PDF and refresh the chat so the revision entry shows
          setPdfVersion((v) => v + 1);
          setActiveChat("internal");
          onChanged?.();
        }}
      />
    </div>
  );
};

export default SubmissionDashboard;
